import { apiUrl } from '../Utility/ApiUrl'

export interface AuthUser {
  email: string
  firstName: null | string
  isMember: boolean
  lastName: null | string
  type: null | UserType
}

export type UserType = 'guest' | 'new' | 'registered'

// Controller errors carry { error, message }; the routing middleware emits RFC 7807
// problem details { type, title, status, detail } — accept both, mirroring CartApiService.
interface RawError {
  detail?: string
  error?: string
  message?: string
  retryAfter?: number
  title?: string
}

interface RequestOtpResponse {
  userType: UserType
}

interface SessionResponse {
  authenticated: boolean
  user?: AuthUser
}

interface VerifyOtpResponse {
  user: AuthUser
}

export class AuthError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    // Seconds until the next attempt is allowed; sent in the body on 429 because
    // the Retry-After header is not readable from these JSON-only responses.
    public readonly retryAfter: null | number = null,
  ) {
    super(message)
  }
}

const toAuthError = (raw: RawError): AuthError =>
  new AuthError(raw.error ?? 'error', raw.message ?? raw.detail ?? raw.title ?? '', raw.retryAfter ?? null)

class AuthApiService {
  async logout(): Promise<void> {
    await this.request('POST', '/api/auth/logout')
  }

  async requestOtp(email: string, guest: boolean): Promise<UserType> {
    const data = await this.request<RequestOtpResponse>('POST', '/api/auth/request-otp', { email, guest })
    return data.userType
  }

  async session(): Promise<AuthUser | null> {
    const data = await this.request<SessionResponse>('GET', '/api/auth/session')
    return data.authenticated ? data.user ?? null : null
  }

  async verifyOtp(email: string, code: string, userType: UserType): Promise<AuthUser> {
    const data = await this.request<VerifyOtpResponse>('POST', '/api/auth/verify-otp', { code, email, userType })
    return data.user
  }

  private async request<T>(method: string, url: string, body?: unknown): Promise<T> {
    const response = await fetch(apiUrl(url), {
      body: body === undefined ? undefined : JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
      method
    })

    const data: unknown = response.status === 204 ? {} : await response.json()

    if (response.ok === false) {
      throw toAuthError(data as RawError)
    }

    return data as T
  }
}

export default new AuthApiService()
